import type { CardId, Seat } from '@basgan/rules';
import type { GameEvent, ViewMessage } from '@basgan/protocol';

/**
 * Görüntü durumu — ekranda NE GÖRÜNDÜĞÜ.
 *
 * Sunucudaki durumun birkaç yüz milisaniye gerisinden gelir: olaylar kuyruğa
 * alınıp sırayla oynatılır, her biri bu durumu ilerletir. Kuyruk boşalınca
 * yetkili `view` ile uzlaştırılır (`reconcile`) — böylece bir sapma olsa bile
 * kendi kendini onarır ve kalıcı hale gelemez.
 *
 * Bu dosyada React, DOM veya ağ yok; saf indirgeyici olduğu için test edilebilir.
 */

export interface PileEntry {
  seat: Seat;
  cards: CardId[];
}

export type Badge = { kind: 'pass' } | { kind: 'finish'; place: number };

export interface DisplayState {
  /** Kendi elimde görünen kartlar. */
  hand: CardId[];
  /** Her koltuğun elinde görünen kart sayısı. */
  counts: number[];
  /** Bu turda masaya atılmış kartlar, atılma sırasıyla. */
  pile: PileEntry[];
  /** Koltuk üstünde gösterilen anlık rozet (PAS / bitirme sırası). */
  badges: Partial<Record<Seat, Badge>>;
  /** El sonu özeti gösteriliyorsa sıralama. */
  dealSummary: { order: Seat[]; scores: number[] } | null;
}

export const initialDisplay = (): DisplayState => ({
  hand: [],
  counts: [0, 0, 0, 0],
  pile: [],
  badges: {},
  dealSummary: null,
});

/**
 * Olay süreleri artık protokolde: sunucu da aynı tabloyu okuyup sıra
 * zamanlayıcısını animasyona göre geciktiriyor. Buradan yeniden dışa
 * aktarılıyor ki istemci tarafındaki çağrılar olduğu yerde kalsın.
 */
export { eventDuration } from '@basgan/protocol';

export function applyEvent(state: DisplayState, event: GameEvent, me: Seat): DisplayState {
  switch (event.kind) {
    case 'play': {
      const counts = [...state.counts];
      counts[event.seat] = Math.max(0, (counts[event.seat] ?? 0) - event.cards.length);
      const played = new Set(event.cards);
      return {
        ...state,
        // Kartlar elden masaya geçiyor; aynı `layoutId` ile uçuşu Motion hallediyor.
        hand: event.seat === me ? state.hand.filter((c) => !played.has(c)) : state.hand,
        counts,
        pile: [...state.pile, { seat: event.seat, cards: event.cards }],
        badges: clearBadge(state.badges, event.seat),
      };
    }

    case 'pass':
      return { ...state, badges: { ...state.badges, [event.seat]: { kind: 'pass' } } };

    case 'cut':
      // Ayrı bir gösterge yok: kesen kart zaten masanın üstünde duruyor.
      return state;

    case 'trickEnd':
      // Masa süpürülür; pas rozetleri de bu turla birlikte silinir.
      return { ...state, pile: [], badges: keepFinishes(state.badges) };

    case 'finish':
      return {
        ...state,
        badges: { ...state.badges, [event.seat]: { kind: 'finish', place: event.place } },
      };

    case 'dealEnd':
      return {
        ...state,
        pile: [],
        dealSummary: { order: event.order, scores: event.scores },
      };

    case 'dealStart':
      return { ...initialDisplay(), counts: state.counts };

    case 'matchStart':
    case 'exchangeStart':
    // Sonuç perdesi kuyruğun dışında, kendi sayacıyla yaşıyor (bkz. `useGame`).
    case 'exchangeDone':
    case 'matchOver':
    case 'presence':
    // Sohbet görüntü durumunun parçası değil: balonlar kuyruğun dışında,
    // kendi süreleriyle yaşıyor (bkz. `useGame`).
    case 'chat':
    // Maskot ve maç sonu kararları da kuyruğun dışında (bkz. `useGame`);
    // görüntü durumuna dokunmuyorlar.
    case 'mascot':
    case 'stayed':
    case 'sold':
      return state;
  }
}

/**
 * Kuyruk boşaldığında yetkili duruma otur.
 * Animasyon sırasında biriken küçük sapmalar burada sıfırlanır.
 */
export function reconcile(state: DisplayState, view: ViewMessage): DisplayState {
  return {
    ...state,
    hand: [...view.hand],
    counts: [...view.handCounts],
    pile: view.trick.plays.map((p) => ({ seat: p.seat, cards: [...p.cards] })),
    // Tur kapandıysa anlık rozetler anlamını yitirir; bitirme rozetleri kalır.
    badges: view.trick.count === 0 ? keepFinishes(state.badges) : state.badges,
  };
}

function clearBadge(
  badges: DisplayState['badges'],
  seat: Seat,
): DisplayState['badges'] {
  if (!badges[seat]) return badges;
  const next = { ...badges };
  delete next[seat];
  return next;
}

/** Bitirme rozetleri el boyunca kalır; PAS/KES anlıktır. */
function keepFinishes(badges: DisplayState['badges']): DisplayState['badges'] {
  const next: DisplayState['badges'] = {};
  for (const [seat, badge] of Object.entries(badges)) {
    if (badge?.kind === 'finish') next[Number(seat) as Seat] = badge;
  }
  return next;
}
